// features/inventory/pages/BulkAdjustPage.tsx
/**
 * Bulk Adjust Page — Upload Excel files to adjust multiple products at once.
 *
 * Route: /inventory/adjust/bulk
 *
 * Features:
 * - Adjustment type selection (correction, waste, damage, found in count)
 * - Template download with instructions
 * - Drag-and-drop file upload with validation
 * - Real-time progress tracking with step indicators
 * - Product matching by name, SKU, or barcode
 * - Comprehensive error reporting with suggested fixes
 * - Success summary with statistics
 * - Download error report for failed rows
 */

import { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Card, CardContent } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { BulkUploadProgress } from '@/components/bulk-upload/BulkUploadProgress';
import { BulkUploadErrors, BulkUploadErrorDetail } from '@/components/bulk-upload/BulkUploadErrors';
import {
    bulkAdjustStock,
    downloadStockTemplate,
} from '@/features/stock/services/stockApi';
import {
    ArrowLeft,
    Upload,
    Download,
    FileSpreadsheet,
    CheckCircle,
    AlertTriangle,
    Info,
    RefreshCw,
    Trash2,
    Sparkles,
    ArrowRight,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { clsx } from 'clsx';

// ============================================================
// CONSTANTS
// ============================================================

interface AdjustmentTypeOption {
    value: string;
    label: string;
    icon: React.ElementType;
    desc: string;
    gradient: string;
}

const ADJUSTMENT_TYPES: AdjustmentTypeOption[] = [
    {
        value: 'correction',
        label: 'Correction',
        icon: RefreshCw,
        desc: 'Fix incorrect stock counts',
        gradient: 'from-blue-500 to-indigo-600',
    },
    {
        value: 'waste',
        label: 'Waste / Spoilage',
        icon: Trash2,
        desc: 'Perishable goods gone bad',
        gradient: 'from-amber-500 to-orange-600',
    },
    {
        value: 'damage',
        label: 'Damaged Goods',
        icon: AlertTriangle,
        desc: 'Physically damaged items',
        gradient: 'from-rose-500 to-red-600',
    },
    {
        value: 'found_in_count',
        label: 'Found in Count',
        icon: CheckCircle,
        desc: 'Discovered during count',
        gradient: 'from-emerald-500 to-teal-600',
    },
];

// ============================================================
// MAIN COMPONENT
// ============================================================

export function BulkAdjustPage() {
    const navigate = useNavigate();
    const user = useAuthStore((s) => s.user);
    const queryClient = useQueryClient();
    const addToast = useToastStore((s) => s.addToast);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // State
    const [adjustmentType, setAdjustmentType] = useState('correction');
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [uploadResult, setUploadResult] = useState<any>(null);
    const [showErrorDetails, setShowErrorDetails] = useState(false);

    // Progress state
    const [bulkUploadState, setBulkUploadState] = useState<{
        isOpen: boolean;
        status: 'processing' | 'completed' | 'failed';
        progress: number;
        steps: Array<{
            id: string;
            label: string;
            icon: React.ElementType;
            status: 'pending' | 'active' | 'completed' | 'error';
        }>;
        result: any;
        errorMessage?: string;
    }>({
        isOpen: false,
        status: 'processing',
        progress: 0,
        steps: [
            {
                id: 'validate',
                label: 'Validating file',
                icon: FileSpreadsheet,
                status: 'pending',
            },
            {
                id: 'match',
                label: 'Matching products',
                icon: FileSpreadsheet,
                status: 'pending',
            },
            {
                id: 'adjust',
                label: 'Adjusting stock',
                icon: RefreshCw,
                status: 'pending',
            },
            {
                id: 'finalize',
                label: 'Finalizing',
                icon: CheckCircle,
                status: 'pending',
            },
        ],
        result: null,
        errorMessage: undefined,
    });

    // Mutations
    const uploadMutation = useMutation({
        mutationFn: async (file: File) => {
            setBulkUploadState((prev) => ({
                ...prev,
                isOpen: true,
                status: 'processing',
                progress: 0,
                steps: prev.steps.map((s) => ({
                    ...s,
                    status: 'pending' as const,
                })),
            }));

            const updateProgress = (stepId: string, progress: number) => {
                setBulkUploadState((prev) => ({
                    ...prev,
                    progress,
                    steps: prev.steps.map((s) => {
                        if (s.id === stepId)
                            return {
                                ...s,
                                status: 'active' as const,
                            };
                        const si = prev.steps.findIndex(
                            (ps) => ps.id === s.id
                        );
                        const ai = prev.steps.findIndex(
                            (ps) => ps.id === stepId
                        );
                        if (si < ai)
                            return {
                                ...s,
                                status: 'completed' as const,
                            };
                        return s;
                    }),
                }));
            };

            updateProgress('validate', 15);
            updateProgress('match', 40);

            const result = await bulkAdjustStock(
                user!.tenantId,
                '',
                adjustmentType,
                file
            );

            updateProgress('adjust', 75);
            updateProgress('finalize', 95);
            return result;
        },
        onSuccess: (result) => {
            setBulkUploadState((prev) => ({
                ...prev,
                progress: 100,
                status: result.hasErrors ? 'failed' : 'completed',
                steps: prev.steps.map((s) => ({
                    ...s,
                    status:
                        s.id === 'finalize' && result.hasErrors
                            ? ('error' as const)
                            : ('completed' as const),
                })),
                result,
            }));
            setUploadResult(result);
            queryClient.invalidateQueries({
                queryKey: ['stock-levels'],
            });
            queryClient.invalidateQueries({
                queryKey: ['stock-transactions'],
            });
            queryClient.invalidateQueries({
                queryKey: ['low-stock-dash'],
            });
            queryClient.invalidateQueries({
                queryKey: ['confirm-status-dash'],
            });
            addToast({
                type: result.hasErrors ? 'warning' : 'success',
                title: 'Bulk adjustment complete',
                message: `${result.successCount} adjusted, ${result.errorCount} errors`,
            });
        },
        onError: (err: any) => {
            setBulkUploadState((prev) => ({
                ...prev,
                progress: 100,
                status: 'failed',
                errorMessage: err?.message,
            }));
            addToast({
                type: 'error',
                title: 'Upload failed',
                message: err?.message,
            });
        },
    });

    // Handlers
    const handleFileSelect = useCallback(
        (file: File) => {
            const ext =
                '.' + file.name.split('.').pop()?.toLowerCase();
            if (!['.xlsx', '.xls', '.csv'].includes(ext)) {
                addToast({
                    type: 'error',
                    title: 'Invalid file',
                    message:
                        'Please upload an Excel or CSV file.',
                });
                return;
            }
            if (file.size > 10 * 1024 * 1024) {
                addToast({
                    type: 'error',
                    title: 'File too large',
                    message: 'Maximum file size is 10MB.',
                });
                return;
            }
            setSelectedFile(file);
        },
        [addToast]
    );

    const handleDownloadTemplate = async () => {
        try {
            const blob = await downloadStockTemplate(
                user!.tenantId,
                'adjustment'
            );
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'stock-adjustment-template.xlsx';
            a.click();
            URL.revokeObjectURL(url);
            addToast({
                type: 'success',
                title: 'Template downloaded',
            });
        } catch {
            addToast({
                type: 'error',
                title: 'Download failed',
            });
        }
    };

    const resetForm = () => {
        setSelectedFile(null);
        setUploadResult(null);
        setShowErrorDetails(false);
    };

    const currentType =
        ADJUSTMENT_TYPES.find((t) => t.value === adjustmentType) ||
        ADJUSTMENT_TYPES[0];

    // ============================================================
    // RENDER
    // ============================================================

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30">
            {/* Header */}
            <div className="bg-white/90 backdrop-blur-md border-b border-slate-200 sticky top-0 z-30 shadow-sm">
                <div className="max-w-4xl mx-auto px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() =>
                                    navigate('/inventory/adjust')
                                }
                                className="p-2 rounded-xl hover:bg-slate-100 transition-colors"
                                type="button"
                            >
                                <ArrowLeft className="w-5 h-5 text-slate-600" />
                            </button>
                            <div
                                className={clsx(
                                    'p-2.5 rounded-2xl shadow-lg bg-gradient-to-br',
                                    currentType.gradient
                                )}
                            >
                                <Upload className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <h1 className="text-xl font-bold text-slate-900">
                                    Bulk Stock Adjustment
                                </h1>
                                <p className="text-sm text-slate-500">
                                    Upload Excel to adjust
                                    multiple products at once
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-4xl mx-auto px-6 lg:px-8 py-8 space-y-6">
                {!uploadResult ? (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="space-y-6"
                    >
                        {/* Adjustment Type */}
                        <Card>
                            <CardContent className="p-6">
                                <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
                                    <Sparkles className="w-4 h-4 text-amber-500" />
                                    Select Adjustment Type
                                </h3>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                    {ADJUSTMENT_TYPES.map(
                                        (type) => (
                                            <motion.button
                                                key={
                                                    type.value
                                                }
                                                type="button"
                                                whileHover={{
                                                    scale: 1.02,
                                                }}
                                                whileTap={{
                                                    scale: 0.98,
                                                }}
                                                onClick={() =>
                                                    setAdjustmentType(
                                                        type.value
                                                    )
                                                }
                                                className={clsx(
                                                    'flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-all text-sm',
                                                    adjustmentType ===
                                                        type.value
                                                        ? 'border-emerald-400 bg-emerald-50 ring-2 ring-emerald-500/20 shadow-sm'
                                                        : 'border-slate-200 hover:border-emerald-300 hover:bg-slate-50'
                                                )}
                                            >
                                                <div
                                                    className={clsx(
                                                        'p-1.5 rounded-lg',
                                                        adjustmentType ===
                                                            type.value
                                                            ? 'bg-emerald-100'
                                                            : 'bg-slate-100'
                                                    )}
                                                >
                                                    <type.icon
                                                        size={
                                                            16
                                                        }
                                                        className={clsx(
                                                            'flex-shrink-0',
                                                            adjustmentType ===
                                                                type.value
                                                                ? 'text-emerald-600'
                                                                : 'text-slate-400'
                                                        )}
                                                    />
                                                </div>
                                                <div>
                                                    <p className="font-semibold text-xs">
                                                        {
                                                            type.label
                                                        }
                                                    </p>
                                                    <p className="text-[10px] text-slate-400">
                                                        {
                                                            type.desc
                                                        }
                                                    </p>
                                                </div>
                                            </motion.button>
                                        )
                                    )}
                                </div>
                            </CardContent>
                        </Card>

                        {/* Upload */}
                        <Card>
                            <CardContent className="p-6 space-y-4">
                                <div className="flex items-center gap-3">
                                    <button
                                        onClick={
                                            handleDownloadTemplate
                                        }
                                        className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 text-sm font-medium text-slate-700 shadow-sm transition-all"
                                        type="button"
                                    >
                                        <Download className="w-4 h-4" />{' '}
                                        Download Template
                                    </button>
                                    <span className="text-xs text-slate-400">
                                        Fill with products and
                                        quantities, then upload
                                    </span>
                                </div>

                                <motion.div
                                    whileHover={{
                                        borderColor:
                                            '#34D399',
                                    }}
                                    onDrop={(e) => {
                                        e.preventDefault();
                                        setIsDragging(false);
                                        const f =
                                            e.dataTransfer
                                                .files?.[0];
                                        if (f)
                                            handleFileSelect(
                                                f
                                            );
                                    }}
                                    onDragOver={(e) => {
                                        e.preventDefault();
                                        setIsDragging(true);
                                    }}
                                    onDragLeave={() =>
                                        setIsDragging(false)
                                    }
                                    className={clsx(
                                        'border-2 border-dashed rounded-2xl p-12 text-center transition-all cursor-pointer',
                                        isDragging
                                            ? 'border-emerald-400 bg-emerald-50/50 scale-[1.02]'
                                            : selectedFile
                                                ? 'border-emerald-300 bg-emerald-50/30'
                                                : 'border-slate-300 hover:border-emerald-400 hover:bg-slate-50'
                                    )}
                                    onClick={() =>
                                        fileInputRef.current?.click()
                                    }
                                >
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept=".xlsx,.xls,.csv"
                                        className="hidden"
                                        onChange={(e) => {
                                            const f =
                                                e.target
                                                    .files?.[0];
                                            if (f) {
                                                handleFileSelect(
                                                    f
                                                );
                                                e.target.value =
                                                    '';
                                            }
                                        }}
                                    />
                                    {selectedFile ? (
                                        <motion.div
                                            initial={{
                                                scale: 0.9,
                                            }}
                                            animate={{
                                                scale: 1,
                                            }}
                                        >
                                            <div className="w-16 h-16 rounded-2xl bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                                                <FileSpreadsheet className="w-8 h-8 text-emerald-600" />
                                            </div>
                                            <h3 className="text-lg font-bold text-slate-700">
                                                {
                                                    selectedFile.name
                                                }
                                            </h3>
                                            <p className="text-sm text-slate-400 mt-1">
                                                {(
                                                    selectedFile.size /
                                                    1024
                                                ).toFixed(
                                                    1
                                                )}{' '}
                                                KB · Click to
                                                change
                                            </p>
                                        </motion.div>
                                    ) : (
                                        <motion.div
                                            animate={
                                                isDragging
                                                    ? {
                                                        scale: 1.05,
                                                    }
                                                    : {
                                                        y: [
                                                            0,
                                                            -5,
                                                            0,
                                                        ],
                                                    }
                                            }
                                            transition={
                                                isDragging
                                                    ? {}
                                                    : {
                                                        duration: 3,
                                                        repeat: Infinity,
                                                        ease: 'easeInOut',
                                                    }
                                            }
                                        >
                                            <div className="w-16 h-16 rounded-2xl bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                                                <Upload className="w-8 h-8 text-emerald-600" />
                                            </div>
                                            <h3 className="text-lg font-bold text-slate-700">
                                                {isDragging
                                                    ? 'Drop your file here'
                                                    : 'Click or drag file to upload'}
                                            </h3>
                                            <p className="text-sm text-slate-400 mt-1">
                                                Excel (.xlsx,
                                                .xls) or CSV ·
                                                Max 10MB
                                            </p>
                                        </motion.div>
                                    )}
                                </motion.div>

                                <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl flex items-start gap-3">
                                    <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                                    <div>
                                        <p className="text-sm font-semibold text-blue-700">
                                            How it works
                                        </p>
                                        <ul className="text-xs text-blue-600 mt-1 space-y-1">
                                            <li>
                                                · Use negative
                                                numbers (e.g.,
                                                -5) to deduct
                                                stock
                                            </li>
                                            <li>
                                                · Use positive
                                                numbers (e.g.,
                                                10) to add
                                                stock
                                            </li>
                                            <li>
                                                · Products
                                                matched by
                                                name, SKU, or
                                                barcode
                                            </li>
                                            <li>
                                                · Failed rows
                                                are skipped
                                                with error
                                                details
                                            </li>
                                        </ul>
                                    </div>
                                </div>

                                {selectedFile && (
                                    <div className="flex items-center gap-3 justify-center">
                                        <button
                                            onClick={() =>
                                                setSelectedFile(
                                                    null
                                                )
                                            }
                                            className="px-5 py-3 border-2 border-slate-300 rounded-xl font-medium text-sm hover:bg-white transition-all"
                                            type="button"
                                        >
                                            Remove
                                        </button>
                                        <motion.button
                                            whileHover={{
                                                scale: 1.02,
                                            }}
                                            whileTap={{
                                                scale: 0.98,
                                            }}
                                            onClick={() =>
                                                uploadMutation.mutate(
                                                    selectedFile
                                                )
                                            }
                                            disabled={
                                                uploadMutation.isPending
                                            }
                                            className={clsx(
                                                'flex items-center gap-2 px-8 py-3 rounded-xl font-semibold shadow-lg disabled:opacity-50 text-white bg-gradient-to-r',
                                                currentType.gradient
                                            )}
                                            type="button"
                                        >
                                            {uploadMutation.isPending ? (
                                                <Spinner
                                                    size="xs"
                                                    variant="white"
                                                />
                                            ) : (
                                                <Upload className="w-4 h-4" />
                                            )}
                                            Process Bulk
                                            Adjustment
                                        </motion.button>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </motion.div>
                ) : (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="space-y-6"
                    >
                        {/* Result Card */}
                        <Card
                            className={clsx(
                                'border-2',
                                uploadResult.hasErrors
                                    ? 'border-amber-200'
                                    : 'border-emerald-200'
                            )}
                        >
                            <CardContent className="p-8 text-center">
                                <motion.div
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    transition={{
                                        type: 'spring',
                                        stiffness: 200,
                                    }}
                                    className={clsx(
                                        'w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-4',
                                        uploadResult.hasErrors
                                            ? 'bg-amber-100'
                                            : 'bg-emerald-100'
                                    )}
                                >
                                    {uploadResult.hasErrors ? (
                                        <AlertTriangle className="w-10 h-10 text-amber-600" />
                                    ) : (
                                        <CheckCircle className="w-10 h-10 text-emerald-600" />
                                    )}
                                </motion.div>
                                <h2 className="text-xl font-bold text-slate-900">
                                    {uploadResult.hasErrors
                                        ? 'Completed with Issues'
                                        : 'Bulk Adjustment Complete!'}
                                </h2>
                                <p className="text-sm text-slate-500 mt-2">
                                    <span className="font-bold text-emerald-600">
                                        {
                                            uploadResult.successCount
                                        }
                                    </span>{' '}
                                    adjusted ·{' '}
                                    <span className="font-bold text-rose-600">
                                        {uploadResult.errorCount}
                                    </span>{' '}
                                    errors ·{' '}
                                    <span className="font-bold text-amber-600">
                                        {uploadResult.skippedCount ||
                                            0}
                                    </span>{' '}
                                    skipped
                                </p>
                            </CardContent>
                        </Card>

                        {/* Stats Grid */}
                        <div className="grid grid-cols-3 gap-3">
                            <motion.div
                                initial={{
                                    opacity: 0,
                                    y: 10,
                                }}
                                animate={{
                                    opacity: 1,
                                    y: 0,
                                }}
                                transition={{ delay: 0.1 }}
                                className="bg-white rounded-2xl border border-slate-200 p-5 text-center shadow-sm"
                            >
                                <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold">
                                    Total Rows
                                </p>
                                <p className="text-3xl font-black text-slate-900 mt-1">
                                    {uploadResult.totalRows}
                                </p>
                            </motion.div>
                            <motion.div
                                initial={{
                                    opacity: 0,
                                    y: 10,
                                }}
                                animate={{
                                    opacity: 1,
                                    y: 0,
                                }}
                                transition={{ delay: 0.2 }}
                                className="bg-emerald-50 rounded-2xl border border-emerald-200 p-5 text-center shadow-sm"
                            >
                                <p className="text-xs text-emerald-600 uppercase tracking-wider font-semibold">
                                    Adjusted
                                </p>
                                <p className="text-3xl font-black text-emerald-700 mt-1">
                                    {
                                        uploadResult.successCount
                                    }
                                </p>
                            </motion.div>
                            <motion.div
                                initial={{
                                    opacity: 0,
                                    y: 10,
                                }}
                                animate={{
                                    opacity: 1,
                                    y: 0,
                                }}
                                transition={{ delay: 0.3 }}
                                className="bg-rose-50 rounded-2xl border border-rose-200 p-5 text-center shadow-sm"
                            >
                                <p className="text-xs text-rose-600 uppercase tracking-wider font-semibold">
                                    Errors
                                </p>
                                <p className="text-3xl font-black text-rose-700 mt-1">
                                    {uploadResult.errorCount}
                                </p>
                            </motion.div>
                        </div>

                        {/* Actions */}
                        <div className="flex justify-center gap-3 flex-wrap">
                            {uploadResult.hasErrors && (
                                <motion.button
                                    whileHover={{
                                        scale: 1.02,
                                    }}
                                    whileTap={{
                                        scale: 0.98,
                                    }}
                                    onClick={() =>
                                        setShowErrorDetails(
                                            true
                                        )
                                    }
                                    className="flex items-center gap-2 px-5 py-2.5 bg-amber-50 border border-amber-200 text-amber-700 rounded-xl text-sm font-semibold hover:bg-amber-100 transition-all"
                                    type="button"
                                >
                                    <AlertTriangle className="w-4 h-4" />{' '}
                                    View Error Details
                                </motion.button>
                            )}
                            <motion.button
                                whileHover={{
                                    scale: 1.02,
                                }}
                                whileTap={{
                                    scale: 0.98,
                                }}
                                onClick={resetForm}
                                className="flex items-center gap-2 px-5 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-medium shadow-sm hover:bg-slate-50 transition-all"
                                type="button"
                            >
                                <Upload className="w-4 h-4" />{' '}
                                New Upload
                            </motion.button>
                            <motion.button
                                whileHover={{
                                    scale: 1.02,
                                }}
                                whileTap={{
                                    scale: 0.98,
                                }}
                                onClick={() =>
                                    navigate(
                                        '/inventory/levels'
                                    )
                                }
                                className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl font-semibold shadow-lg text-sm hover:from-emerald-700 transition-all"
                                type="button"
                            >
                                <ArrowRight className="w-4 h-4" />{' '}
                                Go to Stock
                            </motion.button>
                        </div>
                    </motion.div>
                )}
            </div>

            {/* Progress Modal */}
            <BulkUploadProgress
                isOpen={
                    bulkUploadState.isOpen && !showErrorDetails
                }
                onClose={() => {
                    if (
                        bulkUploadState.status !== 'processing'
                    ) {
                        setBulkUploadState((prev) => ({
                            ...prev,
                            isOpen: false,
                        }));
                        if (
                            bulkUploadState.result?.hasErrors
                        )
                            setShowErrorDetails(true);
                    }
                }}
                steps={bulkUploadState.steps}
                progress={bulkUploadState.progress}
                status={bulkUploadState.status}
                stats={{
                    totalRows:
                        bulkUploadState.result?.totalRows ||
                        0,
                    successCount:
                        bulkUploadState.result
                            ?.successCount || 0,
                    errorCount:
                        bulkUploadState.result?.errorCount ||
                        0,
                    categoriesCreated: 0,
                }}
                errorMessage={bulkUploadState.errorMessage}
            />

            {/* Error Details Modal */}
            <BulkUploadErrors
                isOpen={showErrorDetails}
                onClose={() => setShowErrorDetails(false)}
                errors={
                    (uploadResult?.errors ||
                        []) as BulkUploadErrorDetail[]
                }
                totalRows={uploadResult?.totalRows || 0}
                successCount={uploadResult?.successCount || 0}
                errorCount={uploadResult?.errorCount || 0}
            />
        </div>
    );
}